import { sparkToast } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { DatasetOrigin, TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import {
    initTwoPhaseProgressItem,
    markTwoPhaseUploadCompleteStartProcessing,
} from '@/features/datasets/utils/uploadProgress';

function postJson(endpoint, payload) {
    return new Promise((resolve, reject) => {
        data_from_server_post(
            endpoint,
            payload,
            (response) => resolve(response),
            (error) =>
                reject(
                    new Error(
                        typeof error === 'string'
                            ? error
                            : error?.message || 'Request failed'
                    )
                )
        );
    });
}

export async function fetchHfDatasetColumns({ datasetId, clientId }) {
    const response = await postJson('get-hf-dataset-columns', {
        dataset_id: datasetId,
        client_id: clientId,
    });
    if (!response?.success) {
        throw new Error(response?.error || 'Failed to fetch dataset columns');
    }
    return response.columns || [];
}

export async function fetchHfDatasetLabels({
    datasetId,
    labelColumn,
    clientId,
    isMultilabel = false,
    delimiter,
}) {
    const response = await postJson('get-hf-dataset-labels', {
        dataset_id: datasetId,
        label_column: labelColumn,
        client_id: clientId,
        is_multilabel: isMultilabel,
        delimiter: isMultilabel ? delimiter : undefined,
    });
    if (!response?.success) {
        throw new Error(response?.error || 'Failed to fetch labels');
    }
    return response.labels || [];
}

export const HF_PREVIEW_SAMPLE_LIMIT = 20;

export async function fetchHfDatasetSamples({
    datasetId,
    clientId,
    split,
    limit = HF_PREVIEW_SAMPLE_LIMIT,
}) {
    const response = await postJson('get-hf-dataset-samples', {
        dataset_id: datasetId,
        client_id: clientId,
        split: split || null,
        limit,
    });
    if (!response?.success) {
        throw new Error(response?.error || 'Failed to fetch dataset samples');
    }
    const samples = Array.isArray(response.samples) ? response.samples : [];
    return {
        samples,
        sample: response.sample || samples[0] || {},
    };
}

/** null = unverifiable; true/false = scanned result. */
export async function fetchHfDatasetEmptyValues({
    datasetId,
    column,
    clientId,
    isMultilabel = false,
    delimiter,
    splits = [],
}) {
    const response = await postJson('get-hf-dataset-empty-values', {
        dataset_id: datasetId,
        column,
        client_id: clientId,
        is_multilabel: isMultilabel,
        delimiter: isMultilabel ? delimiter : undefined,
        splits: (splits || []).filter(Boolean),
    });
    if (!response?.success) return null;
    return response.has_empty_values ?? null;
}

export function buildHuggingFaceUploadPayload({
    datasetId,
    trainSplit,
    testSplit,
    metadata,
    usecase,
    taskType,
}) {
    const resolvedTaskType = taskType || usecase?.task_type || TaskType.classification;
    const isSummarization = isGenerativeTask(resolvedTaskType);
    // Agentic tasks only; both keys are omitted otherwise so the payload for
    // every other task type is unchanged.
    const trajectoryFields = {};
    if (metadata?.generateTrajectories !== undefined) {
        trajectoryFields.generate_trajectories = Boolean(metadata.generateTrajectories);
    }
    if (metadata?.emptyTrajectoryHandling) {
        trajectoryFields.empty_trajectory_handling = metadata.emptyTrajectoryHandling;
    }
    return {
        dataset_id: datasetId,
        label_column: metadata?.labelColumnName || '',
        text_column: metadata?.exampleColumnName || 'example',
        label_mappings: isSummarization ? {} : metadata?.labelMappings || {},
        client_id: usecase?.client_id,
        is_benchmark: false,
        task_type: resolvedTaskType,
        usecase_id: usecase?._id,
        data_creation_type:
            metadata?.datasetOrigin && metadata.datasetOrigin !== 'skip'
                ? metadata.datasetOrigin
                : DatasetOrigin.huggingface,
        train_split: trainSplit || null,
        test_split: testSplit || null,
        is_multilabel:
            !isSummarization &&
            usecase?.is_multilabel === true &&
            resolvedTaskType === TaskType.classification,
        delimiter: isSummarization ? null : metadata?.delimiter || null,
        empty_multilabel_handling: metadata?.emptyMultilabelHandling,
        rejection_label_name: metadata?.rejectionLabelName ?? null,
        ...trajectoryFields,
    };
}

function startHfProgress({
    setProgressStatus,
    clearCancelledUploadSuppression,
    key,
    datasetName,
    isBenchmark,
    usecase,
    message,
}) {
    if (!setProgressStatus || !key) return;
    const existedOnUsecaseBeforeUpload = Boolean(
        isBenchmark
            ? (usecase?.benchmarks || []).includes(key)
            : (usecase?.datasets || []).includes(key)
    );
    clearCancelledUploadSuppression?.(key);
    initTwoPhaseProgressItem({
        setProgressStatus,
        key,
        datasetName,
        isBenchmark,
        usecaseId: usecase?._id,
        message,
        existedOnUsecaseBeforeUpload,
    });
    markTwoPhaseUploadCompleteStartProcessing({
        setProgressStatus,
        key,
    });
}

/**
 * Start Hugging Face ingest with Data Collection column mappings.
 * Train split becomes the dataset; test split (when set) becomes the benchmark.
 */
export function uploadHuggingFaceDatasetToTask({
    datasetId,
    trainSplit,
    testSplit,
    metadata,
    usecase,
    taskType,
    setProgressStatus,
    clearCancelledUploadSuppression,
    refreshUsecases,
    onComplete,
}) {
    const payload = buildHuggingFaceUploadPayload({
        datasetId,
        trainSplit,
        testSplit,
        metadata,
        usecase,
        taskType,
    });
    return new Promise((resolve, reject) => {
        data_from_server_post(
            'upload-huggingface-dataset',
            payload,
            (result) => {
                if (!result?.success) {
                    const message = result?.error || 'Upload failed';
                    sparkToast.error(message);
                    reject(new Error(message));
                    return;
                }
                sparkToast.success('Dataset upload started successfully');
                const trainDatasetKey = result.dataset_name || datasetId;
                const benchmarkDatasetKey =
                    result.benchmark_name || (datasetId ? `${datasetId}_test` : null);
                if (trainSplit && trainDatasetKey) {
                    startHfProgress({
                        setProgressStatus,
                        clearCancelledUploadSuppression,
                        key: trainDatasetKey,
                        datasetName: trainDatasetKey,
                        isBenchmark: false,
                        usecase,
                        message: 'Processing Hugging Face dataset',
                    });
                }
                if (testSplit && benchmarkDatasetKey) {
                    startHfProgress({
                        setProgressStatus,
                        clearCancelledUploadSuppression,
                        key: benchmarkDatasetKey,
                        datasetName: benchmarkDatasetKey,
                        isBenchmark: true,
                        usecase,
                        message: 'Processing Hugging Face benchmark',
                    });
                }
                refreshUsecases?.();
                onComplete?.();
                resolve(result);
            },
            (error) => {
                const message = `Upload failed: ${error}`;
                sparkToast.error(message);
                reject(new Error(typeof error === 'string' ? error : message));
            }
        );
    });
}
